import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../shared/contexts/ThemeContext";
import { aguaTheme } from "../theme";
import InstalarAppButton from "./InstalarAppButton";
import useManifestAgua from "../hooks/useManifestAgua";

/**
 * Cascarón común de las tres pantallas de Agua: hero azul, navegación por
 * pestañas y botón de instalar la app. Además activa el manifest propio del
 * módulo mientras el usuario está dentro de /agua (ver useManifestAgua).
 */
export default function AguaShell({ titulo, descripcion, icono, acciones, children }) {
  const { t }      = useTranslation();
  const { theme }  = useTheme();
  const location   = useLocation();
  const isLight    = theme === "light";
  const tema       = aguaTheme(isLight);

  useManifestAgua();

  const TABS = [
    { to: "/agua",           icon: "fa-pen-to-square",  label: t("agua.tabCaptura")   },
    { to: "/agua/historial", icon: "fa-clock-rotate-left", label: t("agua.tabHistorial") },
    { to: "/agua/resumen",   icon: "fa-chart-simple",   label: t("agua.tabResumen")   },
  ];

  return (
    <>
      <style>{`
        @keyframes aguaFadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:none} }
        .agua-wrap { padding: 24px 24px 56px; animation: aguaFadeUp .35s ease both; }
        .agua-tab { text-decoration:none; display:inline-flex; align-items:center; gap:7px;
                    padding:9px 15px; border-radius:10px; font-weight:700; font-size:13px;
                    white-space:nowrap; transition:background .15s, color .15s; }
        .agua-card { background:var(--bg-card); border:1px solid var(--border-card);
                     border-radius:14px; padding:20px; }
        .agua-btn { border:none; border-radius:11px; padding:13px 18px; font-weight:800;
                    font-size:14px; cursor:pointer; display:inline-flex; align-items:center;
                    justify-content:center; gap:8px; transition:opacity .15s, transform .1s; }
        .agua-btn:active { transform: scale(.985); }
        .agua-btn:disabled { opacity:.6; cursor:not-allowed; }
        .agua-grid { display:grid; gap:14px; }
        /* En celular todo va a una columna y con menos aire lateral */
        @media (max-width: 640px) {
          .agua-wrap { padding: 16px 12px 72px; }
          .agua-card { padding:16px; border-radius:12px; }
          .agua-tabs { overflow-x:auto; -webkit-overflow-scrolling:touch; }
          .agua-tabs::-webkit-scrollbar { display:none; }
        }
      `}</style>

      <div className="agua-wrap">
        {/* ── Hero ── */}
        <div style={{
          borderRadius: 16, marginBottom: 20, position: "relative", overflow: "hidden",
          background: tema.heroBg, border: `1px solid ${tema.heroBorder}`,
          boxShadow: tema.heroShadow,
        }}>
          <div style={{
            position: "absolute", top: -60, right: -40, width: 220, height: 220,
            borderRadius: "50%", pointerEvents: "none",
            background: `radial-gradient(circle,${isLight ? "rgba(2,132,199,0.13)" : "rgba(14,165,233,0.16)"} 0%,transparent 70%)`,
          }} />
          <div style={{ position: "relative", padding: "20px 22px" }}>
            <div style={{
              display: "flex", justifyContent: "space-between",
              alignItems: "flex-start", gap: 14, flexWrap: "wrap",
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12, minWidth: 0 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                  background: tema.chipBg, border: `1px solid ${tema.chipBorder}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <i className={`fa-solid ${icono || "fa-droplet"}`}
                     style={{ color: tema.azul, fontSize: 15 }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <span style={{
                    fontSize: 12, fontWeight: 700, color: tema.azul,
                    letterSpacing: 0.6, display: "block", marginBottom: 4,
                    textTransform: "uppercase",
                  }}>
                    {t("agua.modulo")}
                  </span>
                  <h1 style={{
                    fontSize: "clamp(18px,2.5vw,25px)", fontWeight: 800,
                    color: tema.heroText, margin: "0 0 4px",
                  }}>
                    {titulo}
                  </h1>
                  {descripcion && (
                    <p style={{ margin: 0, fontSize: 13, color: tema.heroSub }}>{descripcion}</p>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                {acciones}
                <InstalarAppButton />
              </div>
            </div>

            {/* ── Pestañas ── */}
            <div className="agua-tabs" style={{ display: "flex", gap: 6, marginTop: 18 }}>
              {TABS.map(({ to, icon, label }) => {
                const activo = location.pathname === to;
                return (
                  <Link key={to} to={to} className="agua-tab" style={{
                    background: activo ? tema.azul : (isLight ? "rgba(15,23,42,0.05)" : "rgba(255,255,255,0.07)"),
                    color:      activo ? "#fff"    : tema.heroText,
                    boxShadow:  activo ? "0 4px 14px rgba(2,132,199,0.35)" : "none",
                  }}>
                    <i className={`fa-solid ${icon}`} style={{ fontSize: 12 }} /> {label}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {children}
      </div>
    </>
  );
}
